import { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer'
import ExcelJS from 'exceljs'

import './exportModal.scss'
import { Success, Error } from '../../ModalContent'
import { reportByDateRange } from '../../../utilities/api'
import AuthService from '../../../utilities/services/auth.service'
import { DateInputWrapper } from '../../DateInputWrapper'
import { formatDateTime } from '../../../utilities/helpers'
import moment from 'moment'
import {
  ReportProgressIndicator,
  useReportProgress,
  REPORT_PROGRESS_STAGES,
  EXPORT_FORMAT_STAGE_LABELS,
  isAbortError,
} from '../../ReportProgressIndicator'

// Utility functions for formatting data based on type
const formatCellValue = (value, type) => {
  switch (type) {
    case 'currency':
      return `$${typeof value === 'number' ? value.toFixed(2) : value}`
    case 'percentage':
      return `${value}%`
    case 'boolean':
      return value ? 'Yes' : 'No'
    case 'number':
      return value?.toString() || '0'
    case 'text':
    default:
      return value?.toString() || ''
  }
}

// Universal PDF Generator using JSON schema
const UniversalPDFReport = ({
  schema,
  reportData,
  organizationName,
  dateRange,
}) => {
  const styles = StyleSheet.create({
    page: { padding: 20, fontSize: 8 },
    headerRow: {
      flexDirection: 'row',
      marginBottom: 15,
      alignItems: 'flex-start',
    },
    logoContainer: {
      width: '25%',
      alignItems: 'flex-start',
      justifyContent: 'center',
    },
    logoText: {
      fontSize: 10,
      fontWeight: 'bold',
      backgroundColor: '#EAB308',
      color: 'white',
      padding: 8,
      textAlign: 'center',
    },
    centerContainer: {
      width: '50%',
      alignItems: 'center',
    },
    orgName: {
      fontSize: 16,
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: 5,
    },
    eventSummary: {
      fontSize: 12,
      textAlign: 'center',
      marginBottom: 3,
    },
    dateRange: {
      fontSize: 10,
      textAlign: 'center',
      color: '#666',
    },
    rightContainer: {
      width: '25%',
      alignItems: 'flex-end',
      justifyContent: 'center',
    },
    printedOn: {
      fontSize: 8,
      color: '#666',
      textAlign: 'right',
    },
    timestamp: {
      fontSize: 8,
      color: '#666',
      textAlign: 'right',
      marginTop: 2,
    },
    table: {
      display: 'table',
      width: '100%',
      marginTop: 20,
    },
    tableRow: {
      flexDirection: 'row',
    },
    headerRowTable: {
      flexDirection: 'row',
      backgroundColor: '#f3f4f6',
    },
    headerText: {
      fontSize: 7,
      fontWeight: 'bold',
    },
    cellText: {
      fontSize: 7,
    },
  })

  // Create column styles dynamically from schema
  const columnStyles = {}
  schema.columns.forEach((col, index) => {
    columnStyles[`col${index}`] = {
      width: col.width,
      borderStyle: 'solid',
      borderWidth: 0.5,
      padding: 3,
    }
  })

  const dynamicStyles = StyleSheet.create(columnStyles)

  return (
    <Document>
      <Page size="A4" orientation={schema.orientation} style={styles.page}>
        <View style={styles.headerRow}>
          <View style={styles.logoContainer}>
            <Text style={styles.logoText}>LOGO</Text>
          </View>
          <View style={styles.centerContainer}>
            <Text style={styles.orgName}>{organizationName}</Text>
            <Text style={styles.eventSummary}>{schema.title}</Text>
            <Text style={styles.dateRange}>
              Transaction Data Range: {dateRange}
            </Text>
          </View>
          <View style={styles.rightContainer}>
            <Text style={styles.printedOn}>Printed On:</Text>
            <Text style={styles.timestamp}>{new Date().toLocaleString()}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={[styles.tableRow, styles.headerRowTable]}>
            {schema.columns.map((col, index) => (
              <View key={col.key} style={dynamicStyles[`col${index}`]}>
                <Text style={styles.headerText}>{col.label}</Text>
              </View>
            ))}
          </View>

          {reportData.map((item, rowIndex) => (
            <View key={rowIndex} style={styles.tableRow}>
              {schema.columns.map((col, colIndex) => (
                <View key={col.key} style={dynamicStyles[`col${colIndex}`]}>
                  <Text style={styles.cellText}>
                    {formatCellValue(item[col.key], col.type)}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      </Page>
    </Document>
  )
}

// Helper function to convert column number to Excel column letter(s)
const numberToExcelColumn = (num) => {
  let result = ''
  while (num > 0) {
    num-- // Make it 0-based
    result = String.fromCharCode(65 + (num % 26)) + result
    num = Math.floor(num / 26)
  }
  return result
}

export default function ExportModal({
  reportSchema = {},
  reportType,
  onCancel = () => {},
}) {
  const organization = AuthService.getOrg()[0]
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [asOfDate, setAsOfDate] = useState('')
  const [modalState, setModalState] = useState('')
  const isAsOfReport = reportSchema?.dateMode === 'asOf'
  const progress = useReportProgress()

  // Universal Excel Generator using JSON schema
  const generateExcelFromSchema = async (
    schema,
    reportData,
    organizationName,
    dateRange
  ) => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet(schema.title, {
      properties: { defaultColWidth: 10 },
      pageSetup: {
        orientation: schema.orientation || 'landscape',
        showGridLines: true,
      },
    })

    // Brand styling
    const headerStyle = {
      font: { bold: true, color: { argb: 'FFFFFFFF' } },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFEAB308' },
      },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      },
    }

    const cellStyle = {
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      },
    }

    // Add title rows
    worksheet.addRow([organizationName])
    worksheet.addRow([schema.title])
    worksheet.addRow([`Transaction Data Range: ${dateRange}`])
    worksheet.addRow([`Generated: ${new Date().toLocaleString()}`])
    worksheet.addRow([])

    // Extract headers and data from schema
    const headers = schema.columns.map((col) => col.label)
    const dataRows = reportData.map((item) =>
      schema.columns.map((col) => formatCellValue(item[col.key], col.type))
    )

    // Add headers
    const headerRow = worksheet.addRow(headers)
    headerRow.eachCell((cell) => {
      cell.style = headerStyle
    })

    // Add data rows
    dataRows.forEach((row) => {
      const dataRow = worksheet.addRow(row)
      dataRow.eachCell((cell) => {
        cell.style = cellStyle
      })
    })

    // Auto-fit columns
    worksheet.columns.forEach((column, index) => {
      let maxWidth = headers[index].length
      dataRows.forEach((row) => {
        const cellValue = row[index]?.toString() || ''
        maxWidth = Math.max(maxWidth, cellValue.length)
      })
      column.width = Math.min(maxWidth + 2, 50)
    })

    // Style title rows
    worksheet.getCell('A1').style = {
      font: { bold: true, size: 16 },
      alignment: { horizontal: 'center' },
    }
    worksheet.getCell('A2').style = {
      font: { bold: true, size: 14 },
      alignment: { horizontal: 'center' },
    }

    // Proper column reference calculation
    const lastColumn = numberToExcelColumn(headers.length)

    // Merge title cells with correct column references
    worksheet.mergeCells(`A1:${lastColumn}1`)
    worksheet.mergeCells(`A2:${lastColumn}2`)

    // Generate and download Excel file
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${schema.filename}.xlsx`
    link.click()
    URL.revokeObjectURL(url)
  }

  const generateARAgingSummaryExcel = async (
    schema,
    reportData,
    organizationName,
    asOfLabel
  ) => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet(schema.title, {
      properties: { defaultColWidth: 14 },
      pageSetup: { orientation: schema.orientation || 'landscape' },
    })

    const headerFill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1F4E78' },
    }

    const headerFont = { bold: true, color: { argb: 'FFFFFFFF' } }

    worksheet.mergeCells('A1:G1')
    worksheet.getCell('A1').value = schema.title
    worksheet.getCell('A1').font = { bold: true, size: 16 }

    worksheet.getCell('A2').value = 'Client Name'
    worksheet.getCell('B2').value = organizationName || ''
    worksheet.mergeCells('A3:G3')
    worksheet.getCell('A3').value = `As of ${asOfLabel}`
    worksheet.getCell('A3').font = {
      italic: true,
      color: { argb: 'FF6B7280' },
    }

    worksheet.addRow([])

    const headers = schema.columns.map((col) => col.label)
    const headerRow = worksheet.addRow(headers)
    headerRow.eachCell((cell) => {
      cell.font = headerFont
      cell.fill = headerFill
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      }
    })

    reportData.forEach((row) => {
      const dataRow = worksheet.addRow(
        schema.columns.map((col) => row[col.key] ?? '')
      )
      dataRow.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          cell.numFmt = '$#,##0.00'
          cell.alignment = { horizontal: 'right' }
        } else {
          cell.alignment = { horizontal: 'left' }
        }
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        }
      })

      if (row.customer === 'TOTAL') {
        dataRow.eachCell((cell) => {
          cell.font = { bold: true }
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE5E7EB' },
          }
        })
      }
    })

    worksheet.columns = [
      { width: 26 },
      { width: 14 },
      { width: 14 },
      { width: 14 },
      { width: 14 },
      { width: 16 },
      { width: 14 },
    ]

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${schema.filename}.xlsx`
    link.click()
    URL.revokeObjectURL(url)
  }

  const generatePDFFromSchema = async (
    schema,
    reportData,
    organizationName,
    dateRange
  ) => {
    const doc = (
      <UniversalPDFReport
        schema={schema}
        reportData={reportData}
        organizationName={organizationName}
        dateRange={dateRange}
      />
    )

    const blob = await pdf(doc).toBlob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${schema.filename}.pdf`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleCancelExport = () => {
    progress.cancel()
    setModalState('')
    onCancel()
  }

  const handleExport = async (format) => {
    if (isAsOfReport) {
      if (!asOfDate) {
        alert('Please select an as of date')
        return
      }
    } else if (!fromDate || !toDate) {
      alert('Please select both from and to dates')
      return
    }

    setModalState('loading')
    const controller = progress.start({
      stage: REPORT_PROGRESS_STAGES.PREPARING,
    })

    try {
      progress.setStage(REPORT_PROGRESS_STAGES.LOADING_DATA)
      const reportData = await fetchReportData(
        reportType,
        isAsOfReport ? asOfDate : fromDate,
        isAsOfReport ? asOfDate : toDate,
        controller?.signal
      )

      if (controller?.signal?.aborted) {
        progress.cancel()
        setModalState('')
        return
      }

      const dateRange = isAsOfReport
        ? formatDateTime(moment(asOfDate), 'dateOnly')
        : `${formatDateTime(moment(fromDate), 'dateOnly')} - ${formatDateTime(moment(toDate), 'dateOnly')}`
      const organizationName = organization?.name || 'Blocktickets reports'

      progress.setStage(
        REPORT_PROGRESS_STAGES.GENERATING_EXPORT,
        EXPORT_FORMAT_STAGE_LABELS[format] || EXPORT_FORMAT_STAGE_LABELS.excel
      )

      if (format === 'excel') {
        if (reportType === 'ar-aging-summary') {
          await generateARAgingSummaryExcel(
            reportSchema,
            reportData,
            organizationName,
            dateRange
          )
        } else {
          await generateExcelFromSchema(
            reportSchema,
            reportData,
            organizationName,
            dateRange
          )
        }
      } else {
        await generatePDFFromSchema(
          reportSchema,
          reportData,
          organizationName,
          isAsOfReport ? `As of ${dateRange}` : dateRange
        )
      }

      progress.setStage(REPORT_PROGRESS_STAGES.FINALIZING)
      progress.complete()
      setModalState('success')
    } catch (error) {
      if (isAbortError(error)) {
        progress.complete()
        setModalState('')
        return
      }
      console.error('Export failed:', error)
      progress.fail(error)
      setModalState('error')
    }
  }

  const fetchReportData = async (reportType, fromDate, toDate, signal) => {
    const fromDateISO = fromDate ? fromDate.toISOString() : null
    const toDateISO = toDate ? toDate.toISOString() : null

    if (!fromDateISO || !toDateISO) {
      throw new Error('Both from and to dates are required')
    }

    const res = await reportByDateRange(
      organization?.uuid,
      reportType,
      fromDateISO,
      toDateISO,
      signal
    )

    return res.data
  }

  if (modalState === 'error') {
    return (
      <Error
        title="Error creating report..."
        description=""
        onClose={onCancel}
      />
    )
  }

  if (modalState === 'success') {
    return (
      <Success
        title={'Success!'}
        description={`Your ${reportSchema.displayName} has been generated and downloaded successfully.`}
        onClose={onCancel}
      />
    )
  }

  if (modalState === 'loading') {
    return (
      <Modal.Body className="p-0">
        <ReportProgressIndicator
          active
          variant="modal"
          title="Generating Report..."
          stage={progress.stage}
          message={progress.message}
          percent={progress.percent}
          onCancel={handleCancelExport}
        />
      </Modal.Body>
    )
  }

  return (
    <Modal.Body>
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">
          {reportSchema.displayName || 'Export Report'}
        </h1>
        <p>
          {reportSchema.description ||
            'Generate and download your report in PDF or Excel format.'}
        </p>
      </div>

      <Form className="mb-4">
        <Row>
          {isAsOfReport ? (
            <Col md={6}>
              <DateInputWrapper
                label="As of Date"
                id="asOfDate"
                setDate={setAsOfDate}
                selectedDate={asOfDate}
              />
            </Col>
          ) : (
            <>
              <Col md={6}>
                <DateInputWrapper
                  label="From Date"
                  id="fromDate"
                  setDate={setFromDate}
                  selectedDate={fromDate}
                  maxDate={toDate || undefined}
                />
              </Col>
              <Col md={6}>
                <DateInputWrapper
                  label="To Date"
                  id="toDate"
                  setDate={setToDate}
                  selectedDate={toDate}
                  minDate={fromDate || undefined}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>

      <Stack direction="horizontal" gap={3} className="btn-group-flex">
        <Button
          variant="outline-light"
          size="lg"
          onClick={onCancel}
          disabled={modalState === 'loading'}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={() => handleExport('excel')}
          disabled={modalState === 'loading'}
        >
          Export Excel
        </Button>
      </Stack>
    </Modal.Body>
  )
}
